import React, { useEffect, useState } from "react";
import { Row, Col, Card, Button, Typography, notification, Form, Input, Select, DatePicker, InputNumber, Divider, Spin } from "antd";
import { PlusOutlined, MinusOutlined, ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import { useHistory, useParams } from "react-router-dom";
import { baseUrl } from "../../config";

const { Title, Text } = Typography;
const { Item } = Form;
const { Option } = Select;

const KULRISHI_OPTIONS = [
  { value: 'Uddalak Ji',    label: 'Uddalak Ji (उद्दालक जी)' },
  { value: 'Vasishtha Ji',  label: 'Vasishtha Ji (वशिष्ठ जी)' },
  { value: 'Kashyapa Ji',   label: 'Kashyapa Ji (कश्यप जी)' },
  { value: 'Parasara Ji',   label: 'Parasara Ji (पारासर जी)' },
  { value: 'Piplad Ji',     label: 'Piplad Ji (पिप्लाद जी)' },
  { value: 'Shandilya Ji',  label: 'Shandilya Ji (शांडिल्य जी)' },
  { value: 'Bharadwaj Ji',  label: 'Bharadwaj Ji (भारद्वाज जी)' },
  { value: 'Gautam Rishi',  label: 'Gautam Rishi (गौतम ऋषि)' },
];

const emptyMember = {
  member_name: '', relation_type: undefined, relation_name_with: '',
  gender_type: undefined, dob: null, age: '',
  married_status: undefined, education: undefined, occupation: undefined,
  address: '', phone_no: '', email_address: ''
};

function FamiliesForm() {
  const history = useHistory();
  const { id } = useParams();
  const [form] = Form.useForm();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Location dropdowns
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [panchayats, setPanchayats] = useState([]);
  const [villages, setVillages] = useState([]);

  // Education & occupation for member rows
  const [educations, setEducations] = useState([]);
  const [occupations, setOccupations] = useState([]);

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };

  // Load initial dropdown data
  useEffect(() => {
    axios.get(`${baseUrl}/pop-country/list-all`, { headers: authHeader })
      .then(r => { if (r.data.status) setCountries(r.data.result); });
    axios.get(`${baseUrl}/pop-education/list-all`, { headers: authHeader })
      .then(r => { if (r.data.status) setEducations(r.data.result); });
    axios.get(`${baseUrl}/pop-occupations/list-all`, { headers: authHeader })
      .then(r => { if (r.data.status) setOccupations(r.data.result); });
  }, []);

  // Load family + members for edit
  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({ members: [{ ...emptyMember }], status: '1' });
      return;
    }
    setLoading(true);
    axios.get(`${baseUrl}/pop-families/${id}`, { headers: authHeader })
      .then(async res => {
        if (!res.data.status) return;
        const rec = res.data.result;
        // Load cascading dropdowns in order
        const statesRes = rec.country_id ? await axios.get(`${baseUrl}/pop-states/list-all`, { headers: authHeader, params: { country_id: rec.country_id } }) : null;
        if (statesRes?.data?.status) setStates(statesRes.data.result);
        const distRes = rec.state_id ? await axios.get(`${baseUrl}/pop-districts/list-all`, { headers: authHeader, params: { state_id: rec.state_id } }) : null;
        if (distRes?.data?.status) setDistricts(distRes.data.result);
        const tehRes = rec.district_id ? await axios.get(`${baseUrl}/pop-tehsils/list-all`, { headers: authHeader, params: { district_id: rec.district_id } }) : null;
        if (tehRes?.data?.status) setTehsils(tehRes.data.result);
        const panRes = rec.tehsil_id ? await axios.get(`${baseUrl}/pop-panchayat/list-all`, { headers: authHeader, params: { tehsil_id: rec.tehsil_id } }) : null;
        if (panRes?.data?.status) setPanchayats(panRes.data.result);
        const vilRes = rec.panchayat_id ? await axios.get(`${baseUrl}/pop-village/list-all`, { headers: authHeader, params: { panchayat_id: rec.panchayat_id } }) : null;
        if (vilRes?.data?.status) setVillages(vilRes.data.result);

        // Load existing members
        const memRes = await axios.get(`${baseUrl}/pop-family-member`, { headers: authHeader, params: { limit: 'all', family_id: id } });
        const members = memRes.data.status && memRes.data.result.length > 0
          ? memRes.data.result.map(m => ({
              _id: m.id,
              member_name: m.member_name || '',
              relation_type: m.relation_type || undefined,
              relation_name_with: m.relation_name_with || '',
              gender_type: m.gender_type || undefined,
              dob: m.dob && m.dob !== '0000-00-00' ? moment(m.dob) : null,
              age: m.age || '',
              married_status: m.married_status || undefined,
              education: m.education ? parseInt(m.education) : undefined,
              occupation: m.occupation ? parseInt(m.occupation) : undefined,
              address: m.address || '',
              phone_no: m.phone_no || '',
              email_address: m.email_address || '',
            }))
          : [{ ...emptyMember }];

        form.setFieldsValue({ ...rec, members, status: String(rec.status) });
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Cascade loaders
  const onCountryChange = v => {
    form.setFieldsValue({ state_id: undefined, district_id: undefined, tehsil_id: undefined, panchayat_id: undefined, village_id: undefined });
    setStates([]); setDistricts([]); setTehsils([]); setPanchayats([]); setVillages([]);
    if (v) axios.get(`${baseUrl}/pop-states/list-all`, { headers: authHeader, params: { country_id: v } })
      .then(r => { if (r.data.status) setStates(r.data.result); });
  };
  const onStateChange = v => {
    form.setFieldsValue({ district_id: undefined, tehsil_id: undefined, panchayat_id: undefined, village_id: undefined });
    setDistricts([]); setTehsils([]); setPanchayats([]); setVillages([]);
    if (v) axios.get(`${baseUrl}/pop-districts/list-all`, { headers: authHeader, params: { state_id: v } })
      .then(r => { if (r.data.status) setDistricts(r.data.result); });
  };
  const onDistrictChange = v => {
    form.setFieldsValue({ tehsil_id: undefined, panchayat_id: undefined, village_id: undefined });
    setTehsils([]); setPanchayats([]); setVillages([]);
    if (v) axios.get(`${baseUrl}/pop-tehsils/list-all`, { headers: authHeader, params: { district_id: v } })
      .then(r => { if (r.data.status) setTehsils(r.data.result); });
  };
  const onTehsilChange = v => {
    form.setFieldsValue({ panchayat_id: undefined, village_id: undefined });
    setPanchayats([]); setVillages([]);
    if (v) axios.get(`${baseUrl}/pop-panchayat/list-all`, { headers: authHeader, params: { tehsil_id: v } })
      .then(r => { if (r.data.status) setPanchayats(r.data.result); });
  };
  const onPanchayatChange = v => {
    form.setFieldsValue({ village_id: undefined });
    setVillages([]);
    if (v) axios.get(`${baseUrl}/pop-village/list-all`, { headers: authHeader, params: { panchayat_id: v } })
      .then(r => { if (r.data.status) setVillages(r.data.result); });
  };

  const onFinish = async (values) => {
    setSaving(true);
    try {
      const { members, ...familyData } = values;

      let familyId = id;

      if (isEdit) {
        await axios.put(`${baseUrl}/pop-families/${id}`, familyData, { headers: authHeader });
      } else {
        const res = await axios.post(`${baseUrl}/pop-families`, { ...familyData, status: familyData.status || '1' }, { headers: authHeader });
        familyId = res.data.data?.id;
      }

      // Save members: for edit delete old ones and re-insert, for add just insert
      if (isEdit) {
        // Get existing member IDs
        const existingRes = await axios.get(`${baseUrl}/pop-family-member`, { headers: authHeader, params: { limit: 'all', family_id: familyId } });
        const existing = existingRes.data.status ? existingRes.data.result : [];
        // Delete all existing members
        await Promise.all(existing.map(m => axios.delete(`${baseUrl}/pop-family-member/${m.id}`, { headers: authHeader }).catch(() => {})));
      }

      // Create all members
      if (members && members.length > 0) {
        const validMembers = members.filter(m => m.member_name && m.member_name.trim());
        await Promise.all(validMembers.map(m =>
          axios.post(`${baseUrl}/pop-family-member`, {
            family_id: familyId,
            member_name: m.member_name,
            relation_type: m.relation_type,
            relation_name_with: m.relation_name_with,
            gender_type: m.gender_type,
            dob: m.dob ? moment(m.dob).format('YYYY-MM-DD') : null,
            age: m.age,
            married_status: m.married_status,
            education: m.education,
            occupation: m.occupation,
            address: m.address,
            phone_no: m.phone_no,
            email_address: m.email_address,
            status: '1'
          }, { headers: authHeader })
        ));
      }

      notification.success({ message: isEdit ? 'Family updated successfully' : 'Family added successfully' });
      history.push('/pop-families');
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Error saving record' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  const inputStyle = { borderRadius: 4 };
  const selectStyle = { width: '100%' };

  return (
    <div className="tabled" style={{ padding: '0 0 40px' }}>
      <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ status: '1', members: [{ ...emptyMember }] }}>
        <Card
          style={{ marginBottom: 0 }}
          bodyStyle={{ padding: '24px 32px' }}
          headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>
                {isEdit ? 'Edit Family' : 'Add Family'}
              </span>
              <Button
                icon={<ArrowLeftOutlined />}
                style={{ background: '#fff', borderColor: '#fff', color: '#7a0000', fontWeight: 600 }}
                onClick={() => history.push('/pop-families')}
              >Back</Button>
            </div>
          }
        >
          {/* ── Family Details ── */}
          <Divider orientation="left" style={{ color: '#7a0000', fontWeight: 700, fontSize: 13, borderColor: '#d0dce8', margin: '16px 0 8px' }}>Family Details</Divider>

          <Row gutter={[24, 0]}>
            <Col span={24} md={12}>
              <Item name="head_family" label="Head of Family*" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="Enter Head of Family" style={inputStyle} />
              </Item>
            </Col>
            <Col span={24} md={12}>
              <Item name="head_father_name" label="Head Father Name*" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="Enter Head Father Name" style={inputStyle} />
              </Item>
            </Col>
            <Col span={24} md={12}>
              <Item name="phone_no_head" label="Phone no*" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="Enter Phone no" style={inputStyle} />
              </Item>
            </Col>
            <Col span={24} md={12}>
              <Item name="sub_caste" label="Sub Caste(Gotra)*" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="Enter Sub Caste(Gotra)" style={inputStyle} />
              </Item>
            </Col>
            <Col span={24} md={12}>
              <Item name="kulrishi" label="Kulrishi">
                <Select placeholder="Select Kulrishi" style={selectStyle} allowClear>
                  {KULRISHI_OPTIONS.map(k => <Option key={k.value} value={k.value}>{k.label}</Option>)}
                </Select>
              </Item>
            </Col>
            <Col span={24} md={12}>
              <Item name="kildevi" label="Kuldevi">
                <Input placeholder="Enter Kuldevi" style={inputStyle} />
              </Item>
            </Col>
            <Col span={24} md={12}>
              <Item name="gender" label="Gender*" rules={[{ required: true, message: 'Required' }]}>
                <Select placeholder="Select Gender" style={selectStyle}>
                  <Option value="male">Male</Option>
                  <Option value="female">Female</Option>
                </Select>
              </Item>
            </Col>
          </Row>

          {/* ── Location ── */}
          <Divider orientation="left" style={{ color: '#7a0000', fontWeight: 700, fontSize: 13, borderColor: '#d0dce8', margin: '16px 0 8px' }}>Location</Divider>

          <Row gutter={[24, 0]}>
            <Col span={24} md={12}>
              <Item name="country_id" label="Country*" rules={[{ required: true, message: 'Required' }]}>
                <Select placeholder="Select Country" style={selectStyle} showSearch optionFilterProp="children" onChange={onCountryChange}>
                  {countries.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                </Select>
              </Item>
            </Col>
            <Col span={24} md={12}>
              <Item name="state_id" label="State*" rules={[{ required: true, message: 'Required' }]}>
                <Select placeholder="Select State" style={selectStyle} showSearch optionFilterProp="children" onChange={onStateChange} disabled={states.length === 0}>
                  {states.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
                </Select>
              </Item>
            </Col>
            <Col span={24} md={12}>
              <Item name="district_id" label="District*" rules={[{ required: true, message: 'Required' }]}>
                <Select placeholder="Select District" style={selectStyle} showSearch optionFilterProp="children" onChange={onDistrictChange} disabled={districts.length === 0}>
                  {districts.map(d => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                </Select>
              </Item>
            </Col>
            <Col span={24} md={12}>
              <Item name="tehsil_id" label="City/Tehsil*" rules={[{ required: true, message: 'Required' }]}>
                <Select placeholder="Select City/Tehsil" style={selectStyle} showSearch optionFilterProp="children" onChange={onTehsilChange} disabled={tehsils.length === 0}>
                  {tehsils.map(t => <Option key={t.id} value={t.id}>{t.name}</Option>)}
                </Select>
              </Item>
            </Col>
            <Col span={24} md={12}>
              <Item name="panchayat_id" label="Gram Panchayat*" rules={[{ required: true, message: 'Required' }]}>
                <Select placeholder="Select Gram Panchayat" style={selectStyle} showSearch optionFilterProp="children" onChange={onPanchayatChange} disabled={panchayats.length === 0}>
                  {panchayats.map(p => <Option key={p.id} value={p.id}>{p.name}</Option>)}
                </Select>
              </Item>
            </Col>
            <Col span={24} md={12}>
              <Item name="village_id" label="Village Name*" rules={[{ required: true, message: 'Required' }]}>
                <Select placeholder="Select Village Name" style={selectStyle} showSearch optionFilterProp="children" disabled={villages.length === 0}>
                  {villages.map(v => <Option key={v.id} value={v.id}>{v.name}</Option>)}
                </Select>
              </Item>
            </Col>
            <Col span={24} md={12}>
              <Item name="house_village" label="House in The Village">
                <Input placeholder="-" style={inputStyle} />
              </Item>
            </Col>
            <Col span={24} md={12}>
              <Item name="panchayat_committee" label="Panchayat Committee*" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="Enter Panchayat Committee" style={inputStyle} />
              </Item>
            </Col>
            <Col span={24} md={12}>
              <Item name="pin_code" label="Pin Code*" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="Enter Pin Code" style={inputStyle} />
              </Item>
            </Col>
            <Col span={24} md={12}>
              <Item name="status" label="Status">
                <Select placeholder="Select Status" style={selectStyle}>
                  <Option value="1">Active</Option>
                  <Option value="0">Inactive</Option>
                </Select>
              </Item>
            </Col>
          </Row>

          {/* ── List Member ── */}
          <Divider orientation="left" style={{ color: '#7a0000', fontWeight: 700, fontSize: 13, borderColor: '#d0dce8', margin: '16px 0 8px' }}>List Member</Divider>

          <Form.List name="members">
            {(fields, { add, remove }) => (
              <div>
                {fields.map((field, index) => (
                  <div
                    key={field.key}
                    style={{ background: '#f5f8fb', border: '1px solid #d0dce8', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}
                  >
                    {/* Row 1: Name | Relation | Relation With | Gender | DOB | Age | [+] */}
                    <Row gutter={[8, 0]} align="middle">
                      <Col flex="1 1 140px">
                        <Item name={[field.name, 'member_name']} style={{ marginBottom: 8 }} rules={[{ required: true, message: 'Name required' }]}>
                          <Input placeholder="Name" style={inputStyle} />
                        </Item>
                      </Col>
                      <Col flex="0 0 140px">
                        <Item name={[field.name, 'relation_type']} style={{ marginBottom: 8 }}>
                          <Select placeholder="Relation" style={selectStyle}>
                            <Option value="son_of">Son Of</Option>
                            <Option value="wife_of">Wife Of</Option>
                            <Option value="daughter_of">Daughter Of</Option>
                            <Option value="Self">Self</Option>
                            <Option value="Father">Father</Option>
                            <Option value="Mother">Mother</Option>
                            <Option value="Brother">Brother</Option>
                            <Option value="Sister">Sister</Option>
                            <Option value="Other">Other</Option>
                          </Select>
                        </Item>
                      </Col>
                      <Col flex="1 1 130px">
                        <Item name={[field.name, 'relation_name_with']} style={{ marginBottom: 8 }}>
                          <Input placeholder="Relation With" style={inputStyle} />
                        </Item>
                      </Col>
                      <Col flex="0 0 120px">
                        <Item name={[field.name, 'gender_type']} style={{ marginBottom: 8 }}>
                          <Select placeholder="Gender" style={selectStyle}>
                            <Option value="male">Male</Option>
                            <Option value="female">Female</Option>
                          </Select>
                        </Item>
                      </Col>
                      <Col flex="0 0 140px">
                        <Item name={[field.name, 'dob']} style={{ marginBottom: 8 }}>
                          <DatePicker placeholder="D.O.B" format="YYYY-MM-DD" style={{ width: '100%' }} />
                        </Item>
                      </Col>
                      <Col flex="0 0 80px">
                        <Item name={[field.name, 'age']} style={{ marginBottom: 8 }}>
                          <InputNumber placeholder="Age" style={{ width: '100%' }} min={0} max={150} />
                        </Item>
                      </Col>
                      <Col flex="0 0 40px" style={{ textAlign: 'right' }}>
                        {index === fields.length - 1 && (
                          <Button
                            shape="circle"
                            icon={<PlusOutlined />}
                            size="small"
                            style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
                            onClick={() => add({ ...emptyMember })}
                          />
                        )}
                      </Col>
                    </Row>

                    {/* Row 2: Married | Education | Occupation | Address | Phone | Email | [-] */}
                    <Row gutter={[8, 0]} align="middle">
                      <Col flex="0 0 130px">
                        <Item name={[field.name, 'married_status']} style={{ marginBottom: 0 }}>
                          <Select placeholder="Married Status" style={selectStyle}>
                            <Option value="married">Married</Option>
                            <Option value="unmarried">UnMarried</Option>
                          </Select>
                        </Item>
                      </Col>
                      <Col flex="0 0 140px">
                        <Item name={[field.name, 'education']} style={{ marginBottom: 0 }}>
                          <Select placeholder="Education" style={selectStyle} showSearch optionFilterProp="children" allowClear>
                            {educations.map(e => <Option key={e.id} value={e.id}>{e.name}</Option>)}
                          </Select>
                        </Item>
                      </Col>
                      <Col flex="0 0 140px">
                        <Item name={[field.name, 'occupation']} style={{ marginBottom: 0 }}>
                          <Select placeholder="Occupation" style={selectStyle} showSearch optionFilterProp="children" allowClear>
                            {occupations.map(o => <Option key={o.id} value={o.id}>{o.name}</Option>)}
                          </Select>
                        </Item>
                      </Col>
                      <Col flex="1 1 130px">
                        <Item name={[field.name, 'address']} style={{ marginBottom: 0 }}>
                          <Input placeholder="Current Address" style={inputStyle} />
                        </Item>
                      </Col>
                      <Col flex="1 1 120px">
                        <Item name={[field.name, 'phone_no']} style={{ marginBottom: 0 }}>
                          <Input placeholder="Phone Number" style={inputStyle} />
                        </Item>
                      </Col>
                      <Col flex="1 1 120px">
                        <Item name={[field.name, 'email_address']} style={{ marginBottom: 0 }}>
                          <Input placeholder="Email" style={inputStyle} />
                        </Item>
                      </Col>
                      <Col flex="0 0 40px" style={{ textAlign: 'right' }}>
                        {fields.length > 1 && (
                          <Button
                            danger
                            shape="circle"
                            icon={<MinusOutlined />}
                            size="small"
                            onClick={() => remove(field.name)}
                          />
                        )}
                      </Col>
                    </Row>
                  </div>
                ))}

                {fields.length === 0 && (
                  <Button
                    type="dashed"
                    onClick={() => add({ ...emptyMember })}
                    icon={<PlusOutlined />}
                    block
                  >
                    Add Member
                  </Button>
                )}
              </div>
            )}
          </Form.List>

          {/* ── Submit ── */}
          <Divider style={{ borderColor: '#d0dce8', margin: '20px 0 16px' }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              htmlType="submit"
              loading={saving}
              size="large"
              icon={<SaveOutlined />}
              style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
            >
              {isEdit ? 'Update Family' : 'Save Family'}
            </Button>
            <Button size="large" onClick={() => history.push('/pop-families')}>Cancel</Button>
          </div>
        </Card>
      </Form>
    </div>
  );
}

export default FamiliesForm;
